import React, { useEffect, useRef, useState } from "react";

import ProgressRing from "./ProgressRing";
import editIcon from "../../Images/edit-icon.svg";

export default function CounterBody({
  activeCounter,
  percent,
  dialDotAngleDeg,
  dialRadiusPx,
  onIncrement,
  onEditCounter,
  dailyProgressHint,
  onTapFeedback,
}) {
  const currentCount = activeCounter ? activeCounter.currentCount : 0;

  const [dialPulse, setDialPulse] = useState(false);
  const prevCountRef = useRef(currentCount);

  // -------------------------
  // Pulse animation on count change
  // -------------------------
  useEffect(() => {
    if (prevCountRef.current !== currentCount) {
      setDialPulse(true);

      const t = window.setTimeout(() => setDialPulse(false), 280);
      prevCountRef.current = currentCount;

      return () => window.clearTimeout(t);
    }
  }, [currentCount]);

  // -------------------------
  // Render
  // -------------------------
  return (
    <div className="counterWrapper__body">
      {/* Header */}
      <header
        className="counterBodyHeader"
        aria-label="Dhikr · remembrance"
      >
        <div className="countName">
          <span className="countName__text">
            {activeCounter ? activeCounter.name : "No Active Counter"}
          </span>

          {activeCounter && (
            <button
              type="button"
              className={`countName__edit ${
                activeCounter.isDisabled ? "disabled" : ""
              }`}
              title="Edit"
              aria-label="Edit counter name"
              disabled={activeCounter.isDisabled}
              onClick={() => {
                onTapFeedback?.();
                if (!activeCounter.isDisabled) {
                  onEditCounter(activeCounter.id);
                }
              }}
            >
              <img src={editIcon} alt="" width={18} height={18} />
            </button>
          )}
        </div>
      </header>

      {/* Counter Dial */}
      <div className="counterBeads">
        <div className="counterBeads__glow" aria-hidden="true" />
        <div className="counterBeads__beadRing" aria-hidden="true" />

        <div
          className={`count ${
            activeCounter?.isDisabled ? "disabled" : ""
          }${dialPulse ? " count--pulse" : ""}`}
          onClick={() => {
            onTapFeedback?.();
            onIncrement();
          }}
          role="button"
          tabIndex={activeCounter?.isDisabled ? -1 : 0}
          onKeyDown={(e) => {
            if (activeCounter?.isDisabled) return;

            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onTapFeedback?.();
              onIncrement();
            }
          }}
          aria-label="Tap or press to count dhikr"
        >
          <div className="count__rim" aria-hidden="true" />
          <div className="count__rimInner" aria-hidden="true" />

          <ProgressRing percent={percent} />

          <div className="count-value">
            <span
              className="count-value__innerGlow"
              aria-hidden="true"
            />
            <span
              className="count-value__facet"
              aria-hidden="true"
            />

            <span className="count-value__number">
              {activeCounter ? activeCounter.currentCount : 0}
            </span>

            {/* Dial Indicator */}
            <span
              className="count-percentTrack"
              style={{
                transform: `rotate(${dialDotAngleDeg}deg)`,
                "--countDialRadius": `${dialRadiusPx}px`,
              }}
              aria-hidden
            >
              <span className="count-percent" />
            </span>

            {/* Percentage Text */}
            <span className="count-percent-text">
              <span className="count-percent-text__label">
                Round
              </span>

              <span
                className="count-percent-text__midDot"
                aria-hidden
              />

              {percent}
              <span className="percentSymb">%</span>
            </span>
          </div>
        </div>

        <p className="counterBeads__hint">
          Touch the circle to count each dhikr
        </p>
      </div>

      {/* Daily Progress Hint */}
      {dailyProgressHint && (
        <div
          className={`dailyProgressHint dailyProgressHint--${dailyProgressHint.tone}`}
          role="status"
          aria-live="polite"
          aria-label={dailyProgressHint.message}
        >
          <span
            className="dailyProgressHint__glow"
            aria-hidden
          />

          <span
            className="dailyProgressHint__icon"
            aria-hidden
          >
            {dailyProgressHint.tone === "ahead" ? (
              <svg
                viewBox="0 0 24 24"
                width="22"
                height="22"
                fill="none"
              >
                <path
                  d="M12 3l1.8 5.5h5.8l-4.7 3.4 1.8 5.5L12 14.9 7.3 17.4l1.8-5.5L4.4 8.5h5.8L12 3z"
                  stroke="currentColor"
                  strokeWidth="1.3"
                  strokeLinejoin="round"
                />
              </svg>
            ) : dailyProgressHint.tone === "complete" ? (
              <svg
                viewBox="0 0 24 24"
                width="22"
                height="22"
                fill="none"
              >
                <path
                  d="M6 12.5l3.5 3.5L18 7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg
                viewBox="0 0 24 24"
                width="22"
                height="22"
                fill="none"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="8"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <path
                  d="M12 8v5l3 2"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </span>

          <div className="dailyProgressHint__body">
            <p className="dailyProgressHint__title">
              {dailyProgressHint.title}
            </p>
            <p className="dailyProgressHint__subtitle">
              {dailyProgressHint.subtitle}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}